import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Book, Bug } from 'lucide-react'
import IssueReportModal from './IssueReportModal'
import ryuuImage from '@/assets/ryuu.png'

interface Position {
  x: number
  y: number
}

const STORAGE_KEY = 'ryuu-position'
const DEFAULT_POSITION: Position = { x: 20, y: 20 } // 20px from bottom-right

export default function RyuuAssistant() {
  const navigate = useNavigate()
  const [position, setPosition] = useState<Position>(() => {
    // Initialize state from localStorage
    const savedPosition = localStorage.getItem(STORAGE_KEY)
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition)
        return parsed
      } catch (e) {
        console.error('🐉 Ryuu: Failed to parse saved position:', e)
      }
    }
    return DEFAULT_POSITION
  })
  const [isDragging, setIsDragging] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const ryuuRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  const mouseDownPos = useRef({ x: 0, y: 0 })

  // Save position to localStorage when it changes (but not on first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position))
  }, [position])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMenu &&
        menuRef.current &&
        ryuuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !ryuuRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if menu is open
    if (showMenu) {
      console.log('🐉 Ryuu: Menu is open, ignoring mouseDown')
      return
    }

    if (!ryuuRef.current) return

    // Store initial mouse position
    mouseDownPos.current = { x: e.clientX, y: e.clientY }

    const rect = ryuuRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
    setIsDragging(true)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    // Calculate the new position
    // Since we position from right/bottom, we need to calculate the distance from those edges
    const leftPosition = e.clientX - dragOffset.x
    const topPosition = e.clientY - dragOffset.y
    
    // Convert to right/bottom positioning
    const newX = window.innerWidth - leftPosition - 80 // 80 is Ryuu's width
    const newY = window.innerHeight - topPosition - 80 // 80 is Ryuu's height

    // Keep Ryuu within viewport bounds
    const clampedX = Math.max(0, Math.min(newX, window.innerWidth - 80))
    const clampedY = Math.max(0, Math.min(newY, window.innerHeight - 80))

    setPosition({ x: clampedX, y: clampedY })
  }

  const handleMouseUp = (e: MouseEvent) => {
    if (isDragging) {
      setIsDragging(false)
      
      // Calculate how far the mouse moved from initial position
      const distance = Math.sqrt(
        Math.pow(e.clientX - mouseDownPos.current.x, 2) +
        Math.pow(e.clientY - mouseDownPos.current.y, 2)
      )
      
      console.log('🐉 Ryuu: Mouse up, distance moved:', distance)
      
      // If the mouse didn't move much (less than 5px), treat it as a click
      if (distance < 5) {
        console.log('🐉 Ryuu: Click detected, opening menu')
        setShowMenu(true)
      } else {
        console.log('🐉 Ryuu: Drag ended, final position:', position)
      }
    }
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset, position])

  const handleViewDocs = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('🐉 Ryuu: View Documentation clicked, navigating to /docs')
    setShowMenu(false)
    navigate('/docs')
  }

  const handleReportIssue = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('🐉 Ryuu: Report Issue clicked, opening modal')
    setShowMenu(false)
    setShowIssueModal(true)
  }

  return (
    <>
      <div
        ref={ryuuRef}
        className={`fixed z-[100] transition-all ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab hover:scale-105'}`}
        style={{
          right: `${position.x}px`,
          bottom: `${position.y}px`,
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="relative">
          <img
            src={ryuuImage}
            alt="Ryuu Assistant"
            className="w-20 h-20 object-contain select-none"
            draggable={false}
          />
          
          {/* Pulse animation when not dragging */}
          {!isDragging && !showMenu && (
            <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping" />
          )}
        </div>

        {/* Help Menu */}
        {showMenu && !isDragging && (
          <div
            ref={menuRef}
            className="absolute bottom-full mb-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] animate-in fade-in slide-in-from-bottom-2 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Hi! I'm Ryuu 👋</p>
              <p className="text-xs text-gray-500">How can I help?</p>
            </div>
            
            <button
              onClick={handleViewDocs}
              className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 text-sm"
            >
              <Book className="w-4 h-4 text-blue-600" />
              <span className="text-gray-700">View Documentation</span>
            </button>
            
            <button
              onClick={handleReportIssue}
              className="w-full px-4 py-2.5 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-sm"
            >
              <Bug className="w-4 h-4 text-red-600" />
              <span className="text-gray-700">Report an Issue</span>
            </button>
            
            <div className="px-4 py-2 border-t border-gray-100 mt-1">
              <p className="text-xs text-gray-400">💡 Tip: Drag me anywhere!</p>
            </div>
          </div>
        )}
      </div>

      {/* Issue Report Modal */}
      {showIssueModal && (
        <IssueReportModal
          isOpen={showIssueModal}
          onClose={() => setShowIssueModal(false)}
        />
      )}
    </>
  )
}

